import Controller from 'controllers/controller';
import Binder from 'tools/binder';
import Animator from 'tools/animator';
import {Power0} from 'tools/gsap';
import Elements from 'tools/elements';

class MenuButtonController extends Controller {
    /**
     * Controller for a single .menu-toggle.
     * 
     * This controller is instantiated (in 'controllers/menu-buttons') for each layer of buttons in the header.
     * 
     * @param app
     * @param button
     */
    constructor(app, button) {
        super(app);
        
        this.options = {
            duration: 0.2,
            stagger: 0.1,
            easePlugin: Power0
        };
        
        this.binder = new Binder(this);
        this.elements = new Elements;
        this.elements.save('button', button);
        this.animator = new Animator({
            elements: this.elements.all(),
            duration: this.options.duration
        });
        
        this.cacheElements();
        this.attachEvents();
    }
    
    cacheElements() {
        this.elements.queryAll('closed', 'span.closed', 'button');
        this.elements.queryAll('open', 'span.open', 'button');
        this.elements.queryAll('lines', 'span.line', 'button');
    }
    
    attachEvents() {
        this.app.menuController.bus.on('open', this.binder.bind('open'));
        this.app.menuController.bus.on('close', this.binder.bind('close'));
        this.app.headerController.sharedEvents.menuButton.on('mouseenter', this.binder.bind('onMouseEnter'));
        this.app.headerController.sharedEvents.menuButton.on('mouseleave', this.binder.bind('onMouseLeave'));
    }
    
    removeEvents() {
        this.app.menuController.bus.off('open', this.binder.getBound('open'));
        this.app.menuController.bus.off('close', this.binder.getBound('close'));
        this.app.headerController.sharedEvents.menuButton.off('mouseenter', this.binder.bind('onMouseEnter'));
        this.app.headerController.sharedEvents.menuButton.off('mouseleave', this.binder.bind('onMouseLeave'));
    }

    onMouseEnter() {
        this.animateLines();
    }
    
    onMouseLeave() {
        
    }
    
    animateLines() {
        let props = {
            top: {
                out: { x: '-100%' },
                between: { x: '100%' },
                in: { x: '0%' }
            },
            right: {
                out: { y: '-100%' },
                between: { y: '100%' },
                in: { y: '0%' }
            },
            bottom: {
                out: { x: '100%' },
                between: { x: '-100%' },
                in: { x: '0%' }
            },
            left: {
                out: { y: '100%' },
                between: { y: '-100%' },
                in: { y: '0%' }
            }
        };
        
        this.elements.get('lines').forEach((line, index) => {
            let side = null;
            
            side = line.classList.contains('line-top') ? 'top' : side;
            side = line.classList.contains('line-right') ? 'right' : side;
            side = line.classList.contains('line-bottom') ? 'bottom' : side;
            side = line.classList.contains('line-left') ? 'left' : side;

            this.animateLine(line, props[side], 0.3, index * this.options.stagger);
        });
    }
    
    animateLine(line, props, duration, delay) {
        this.animator.animate(line, Object.assign(props.out, {
            ease: this.options.easePlugin.easeIn,
            delay: delay,
            onComplete: () => {
                this.animator.set(line, props.between);
                this.animator.animate(line, Object.assign(props.in, {
                    ease: this.options.easePlugin.easeOut
                }), duration);
            }
        }), duration);
    }
    
    animateLettersOpen() {
        this.animator.set('closed', {
            rotationY: 0,
            opacity: 1
        });

        this.animator.set('open', {
            rotationY: 90,
            opacity: 0
        });

        this.elements.each('closed', (closedQuarter, index) => {
            let openQuarter = this.elements.get('open')[index];

            setTimeout(() => {
                this.animator.animate(closedQuarter, {
                    rotationY: 90,
                    ease: this.options.easePlugin.easeIn,
                    onComplete: () => {
                        this.animator.set(closedQuarter, {
                            opacity: 0
                        });

                        this.animator.set(openQuarter, {
                            opacity: 1
                        });

                        this.animator.animate(openQuarter, {
                            rotationY: 0,
                            ease: this.options.easePlugin.easeOut
                        })
                    }
                });
            }, this.options.stagger * 1000 * index);
        });
    }
    
    animateLettersClosed() {
        this.animator.set('closed', {
            rotationY: 90,
            opacity: 0
        });

        this.animator.set('open', {
            rotationY: 0,
            opacity: 1
        });

        this.elements.each('open', (openQuarter, index) => {
            let closedQuarter = this.elements.get('closed')[index];

            setTimeout(() => {
                this.animator.animate(openQuarter, {
                    rotationY: 90,
                    ease: this.options.easePlugin.easeIn,
                    onComplete: () => {
                        this.animator.set(openQuarter, {
                            opacity: 0
                        });

                        this.animator.set(closedQuarter, {
                            opacity: 1
                        });

                        this.animator.animate(closedQuarter, {
                            rotationY: 0,
                            ease: this.options.easePlugin.easeOut
                        })
                    }
                });
            }, this.options.stagger * 1000 * index);
        });
    }
    
    open() {
        this.animateLettersOpen();
    }
    
    close() {
        this.animateLettersClosed();
    }
    
    destroy() {
        this.removeEvents();
        this.elements.destroy();
        this.binder.destroy();
        this.animator.destroy();
    }
}

export default MenuButtonController;